import { CellRendererProps, useCell } from "components/Bable"
import React, { useContext, useRef, useState } from "react"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import ConfirmationModal from "components/Modals/ConfirmationModal"
import { CurrencyContext } from "./index"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ExchangeRatePayload } from "models/exchangeRates"
import { ExchangeRateWithId } from "./index"
import Options from "components/inputs/Options"
import { TextInput } from "components/inputs/TextInput"
import { currency_options } from "utils/currencies"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"
import { useRow } from "components/Bable/data/useRow"

// Delete button for each row
const DeleteButton: React.FC<{ row: ExchangeRatePayload }> = ({ row }) => {
  const { onDelete } = useContext(CurrencyContext)
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const { t } = useI18n()

  const handleDeleteClick = () => {
    setDialogOpen(true)
  }

  const handleCancel = () => {
    setDialogOpen(false)
    setDeleteError(null)
  }

  const handleConfirmDelete = async () => {
    setIsSubmitting(true)
    setDeleteError(null)

    try {
      await onDelete({
        clientId: row.clientId,
        sourceCurrency: row.sourceCurrency,
        targetCurrency: row.targetCurrency,
      })
      setDialogOpen(false)
    } catch (error) {
      setDeleteError(t("admin.exchange_rate.delete_error_message"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <DeleteIcon onClick={handleDeleteClick} className={styles.deleteCursor} />
      <ConfirmationModal
        open={isDialogOpen}
        onCancel={handleCancel}
        onSubmit={handleConfirmDelete}
        type="primary_destructive"
        title={t("admin.exchange_rate.delete_exchange_rate")}
        description={`${t("admin.exchange_rate.confirm_deletion")} ${
          row.sourceCurrency
        } – ${row.targetCurrency}?`}
        cancelLabel={t("admin.exchange_rate.cancel")}
        submitLabel={t("admin.exchange_rate.delete")}
        isSubmitDisabled={isSubmitting}
        error={deleteError}
      />
    </>
  )
}

const DeleteButtonCell: React.FC<CellRendererProps> = (props) => {
  const { rowIndex } = props // Get the row index
  const { exchangeRates } = useContext(CurrencyContext)

  if (!exchangeRates || rowIndex < 0 || rowIndex >= exchangeRates.length) {
    return null // Handle the case when exchangeRates is undefined or rowIndex is out of bounds
  }

  // Retrieve the correct row data using the rowIndex
  const row: ExchangeRatePayload = exchangeRates[rowIndex]

  if (!row) {
    return null // Handle the case when row data is not available
  }

  // Pass the actual row data to DeleteButton
  return <DeleteButton row={row} />
}

// EditableCell for Exchange Rate Column
const EditableCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { onUpdate } = useContext(CurrencyContext) // Use the same context
  const { meta } = useAuthenticated()
  const isAgencyAdmin = meta?.userRole === "AGENCY_ADMINISTRATOR"
  const [value, setValue] = useCell(rowId, "exchangeRate")
  const [localError, setLocalError] = useState<boolean>(false)
  const row = useRow(rowId).value as ExchangeRateWithId
  const old = useRef(value)

  const isValidNumber = (val: string) => {
    // Regex to allow only numbers and one optional dot for decimals
    return /^-?\d*(\.\d*)?$/.test(val)
  }
  const formatter = formatFloatingPointNumber(10, 2)
  const hasError = localError && formatter(old.current) !== formatter(value)

  const rifm = useRifm({
    value: !!value ? `${value}` : "",
    accept: /[\d.]/g,
    onChange: (v) => onChange(parseNumber(v)),
    format: (v) => (!!v ? formatter(v) : ""),
  })

  const onChange = (newValue: string) => {
    setLocalError(false)
    // Validate if the new value is a valid number before setting it
    if (isValidNumber(newValue)) {
      setValue(newValue)
    }
  }

  const onBlur = async () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      try {
        setLocalError(false) // Clear the error if the update is successful
        await onUpdate(row, dataKey, value as string)
        old.current = value
      } catch (error: any) {
        // Set the local error if backend validation fails
        setLocalError(true)
      }
    }
  }

  return (
    <>
      <TextInput
        value={rifm.value}
        onChange={rifm.onChange}
        onBlur={onBlur}
        fullWidth
        className={cx(styles.input, styles.editableInput, {
          [styles.error]: hasError,
        })}
        disabled={isAgencyAdmin}
        error={hasError}
      />
    </>
  )
}

export const SelectCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { getFilteredCurrencyOptions, onUpdate } = useContext(CurrencyContext)
  const [value, setValue] = useCell(rowId, dataKey)
  const [localError, setLocalError] = useState<boolean>(false)
  const { meta } = useAuthenticated()
  const isAgencyAdmin = meta?.userRole === "AGENCY_ADMINISTRATOR"
  const row = useRow(rowId).value as ExchangeRateWithId

  if (!row) {
    return null
  }

  const options =
    dataKey === "sourceCurrency"
      ? currency_options
      : getFilteredCurrencyOptions(row.sourceCurrency)

  const handleChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setLocalError(false)
    setValue(value)
  }

  const onBlur = async () => {
    if (dataKey !== undefined && !!value) {
      setLocalError(false) // Clear the error if the update is successful
      try {
        await onUpdate(row, dataKey as string, value as string)
      } catch (error: any) {
        // Set the local error if backend validation fails
        setLocalError(true)
      }
    }
  }

  return (
    <div className={styles.buttonContainer}>
      <Options
        name={dataKey || ""}
        value={value}
        options={options}
        disabled={isAgencyAdmin}
        onChange={handleChange}
        onBlur={onBlur}
        className={cx(styles.ghostSelect, {
          [styles.error]: localError,
        })}
        size="medium"
        InputProps={{
          classes: { root: styles.input },
        }}
      />
    </div>
  )
}

// import Options from "components/inputs/Options"

export const columns = [
  {
    id: "sourceCurrency",
    dataKey: "sourceCurrency",
    Header: "Base Currency",
    accessor: "sourceCurrency",
    sortable: true,
    Cell: SelectCell,
  },
  {
    id: "targetCurrency",
    dataKey: "targetCurrency",
    Header: "Currency",
    accessor: "targetCurrency",
    sortable: true,
    Cell: SelectCell,
  },
  {
    id: "exchangeRate",
    dataKey: "exchangeRate",
    Header: "Exchange Rate",
    accessor: "exchangeRate",
    sortable: true,
    Cell: EditableCell,
  },
  {
    id: "actions",
    dataKey: "actions",
    Header: "Actions",
    accessor: "actions",
    Cell: (props: CellRendererProps) => <DeleteButtonCell {...props} />,
  },
]

const styles = {
  checkbox: css`
    > span > svg {
      width: var(--spacing-xl);
      height: var(--spacing-xl);
    }
  `,
  cell: css`
    display: flex;
    flex-direction: column;
    line-height: 0.7;
    width: 100%;
  `,
  input: css`
    font-size: 13px;
  `,
  deleteCursor: css`
    cursor: pointer;
  `,
  grid: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: var(--spacing-xxs);
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);

      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      border-color: var(--primary-main, teal);

      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
      padding-right: var(--spacing-l);
    }

    .MuiSelect-iconOpen {
      display: block;
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: 1px solid transparent;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    --btn-bg-hover: transparent;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);

    .label {
      justify-content: flex-start;
    }

    &:focus {
      box-shadow: none;
    }

    &:hover {
      background-color: transparent;
      border: 1px solid transparent;
    }
  `,
  buttonHover: css`
    &:hover {
      border: 1px solid var(--primary-main);
    }
  `,
  error: css`
    color: var(--red);
    border: 1px solid var(--red);
  `,
  errorMsg: css`
    margin: 0 5px;
  `,
  mediatypeWidth: css`
    width: 100%;
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
  editableInput: css`
    outline: none;
    transition: border-color 0.3s ease;
    fieldset {
      border: none; /* Remove the border of the fieldset */
    }

    .MuiOutlinedInput-notchedOutline {
      border: none; /* Remove the notched outline, if any */
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border: 1px solid var(--primary-main, teal); /* Add hover border */
    }

    &:focus-within .MuiOutlinedInput-notchedOutline {
      border: 1px solid var(--primary-main, teal); /* Add border on focus */
    }
  `,
}
